import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate, useLocation } from "react-router-dom";
import { API, assetSrc } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { AppHeader } from "@/components/AppHeader";
import { Plus, Image as ImageIcon, Globe, Article, Layout as LayoutIcon, MagnifyingGlass, Package, Trash, FloppyDisk, Newspaper, Buildings, Warning, CheckCircle, UploadSimple, ArrowUp, ArrowDown, Eye, EyeSlash, Tag } from "@phosphor-icons/react";

const LOC_LABEL = (t) => ({ carousel: t.locCarousel, middle_left: t.locLeft, middle_right: t.locRight });

const ComingSoon = ({ testid }) => {
  const { t } = useLang();
  return (
    <div data-testid={testid} className="flex flex-col items-center justify-center border border-dashed border-border bg-card px-4 py-20 text-center">
      <Article size={40} weight="duotone" className="mb-4 text-primary" />
      <div className="font-display text-xl font-black tracking-tight text-foreground">{t.comingSoon}</div>
      <p className="mt-1 text-sm text-muted-foreground">{t.comingSoonDesc}</p>
    </div>
  );
};

const ProductPicker = ({ testid, onPick, excludeIds = [] }) => {
  const { t, lang } = useLang();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const tm = setTimeout(() => {
      axios.get(`${API}/admin/products`, { params: { q, per_page: 10 } })
        .then((r) => setResults((r.data.items || []).filter((x) => !excludeIds.includes(x.product_id))))
        .catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(tm);
  }, [q, excludeIds]);
  return (
    <div className="relative">
      <div className="flex items-center border border-border bg-background px-3 focus-within:border-primary">
        <MagnifyingGlass size={16} weight="bold" className="text-muted-foreground" />
        <input data-testid={testid} value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 180)}
          placeholder={t.searchProduct} className="w-full bg-transparent px-2 py-2.5 text-sm outline-none placeholder-muted-foreground" />
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto border border-border bg-card shadow-lg">
          {results.map((r) => (
            <button key={r.product_id} onMouseDown={(e) => e.preventDefault()} onClick={() => { onPick(r); setQ(""); setResults([]); }}
              className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-primary hover:text-white">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden border border-border bg-white">
                {r.image ? <img src={assetSrc(r.image)} alt="" className="h-full w-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <Package size={12} />}
              </div>
              <span className="min-w-0"><span className="block truncate text-sm font-medium">{r.name?.[lang] || r.name?.fr}</span><span className="block truncate font-mono text-[11px] opacity-70">{r.product_code}</span></span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const SectionEditor = ({ testid, heading, cfg, onChange }) => {
  const { t, lang } = useLang();
  const set = (patch) => onChange({ ...cfg, ...patch });
  const addProd = (r) => {
    if ((cfg.product_ids || []).includes(r.product_id)) return;
    set({ product_ids: [...(cfg.product_ids || []), r.product_id], selected: [...(cfg.selected || []), { product_id: r.product_id, product_code: r.product_code, name: r.name, image: r.image }] });
  };
  const removeProd = (pid) => set({ product_ids: (cfg.product_ids || []).filter((x) => x !== pid), selected: (cfg.selected || []).filter((x) => x.product_id !== pid) });
  return (
    <div className="border border-border bg-card p-5" data-testid={testid}>
      <div className="mb-4 flex items-center gap-2 font-display text-base font-black tracking-tight text-foreground"><LayoutIcon size={18} weight="bold" className="text-primary" /> {heading}</div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label><div className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{t.titleFr}</div><input data-testid={`${testid}-title-fr`} value={cfg.title_fr || ""} onChange={(e) => set({ title_fr: e.target.value })} className="w-full border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" /></label>
        <label><div className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{t.titleEn}</div><input data-testid={`${testid}-title-en`} value={cfg.title_en || ""} onChange={(e) => set({ title_en: e.target.value })} className="w-full border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" /></label>
        <label><div className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{t.fillMode}</div>
          <select data-testid={`${testid}-mode`} value={cfg.mode || "newest"} onChange={(e) => set({ mode: e.target.value })} className="w-full border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary">
            <option value="manual">{t.modeManual}</option>
            <option value="newest">{t.modeNewest}</option>
            <option value="random">{t.modeRandom}</option>
          </select></label>
        <label><div className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{t.limitField}</div><input data-testid={`${testid}-limit`} type="number" min="1" max="12" value={cfg.limit || 6} onChange={(e) => set({ limit: Math.min(12, Math.max(1, Number(e.target.value) || 6)) })} className="w-full border border-border bg-background px-3 py-2 text-sm tabular-nums outline-none focus:border-primary" /></label>
      </div>
      {cfg.mode === "manual" && (
        <div className="mt-4 space-y-2">
          <ProductPicker testid={`${testid}-search`} onPick={addProd} excludeIds={cfg.product_ids || []} />
          {(cfg.selected || []).length === 0 ? (
            <div className="border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground">{t.noSimilar}</div>
          ) : (
            <div className="divide-y divide-border border border-border">
              {(cfg.selected || []).map((s) => (
                <div key={s.product_id} className="flex items-center gap-3 p-2">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden border border-border bg-white">{s.image ? <img src={assetSrc(s.image)} alt="" className="h-full w-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <Package size={14} />}</div>
                  <div className="min-w-0 flex-1"><div className="truncate text-sm font-medium">{s.name?.[lang] || s.name?.fr}</div><div className="truncate font-mono text-[11px] text-primary">{s.product_code}</div></div>
                  <button data-testid={`${testid}-remove-${s.product_id}`} onClick={() => removeProd(s.product_id)} className="border border-red-400 p-1.5 text-red-500 hover:bg-red-500 hover:text-white"><Trash size={14} weight="bold" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const LayoutTab = () => {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { axios.get(`${API}/admin/home-sections`).then((r) => setData(r.data)).catch(() => setData({})); }, []);
  const save = async () => {
    setSaving(true);
    const clean = (c) => ({ title_fr: c.title_fr || "", title_en: c.title_en || "", mode: c.mode || "newest", limit: c.limit || 6, product_ids: c.product_ids || [] });
    try {
      await axios.put(`${API}/admin/home-sections`, { new: clean(data.new), featured: clean(data.featured) });
      toast.success(t.saved);
    } catch (e) { toast.error(e.response?.data?.detail || t.saveFailed); }
    finally { setSaving(false); }
  };
  if (data === null) return <div className="py-16 text-center text-muted-foreground">{t.loading}</div>;
  return (
    <div className="space-y-5" data-testid="layout-tab">
      <div className="flex justify-end">
        <button data-testid="save-layout-button" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-primary px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60"><FloppyDisk size={16} weight="bold" /> {t.save}</button>
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <SectionEditor testid="section-new" heading={t.newProducts} cfg={data.new || {}} onChange={(c) => setData((d) => ({ ...d, new: c }))} />
        <SectionEditor testid="section-featured" heading={t.featuredProducts} cfg={data.featured || {}} onChange={(c) => setData((d) => ({ ...d, featured: c }))} />
      </div>
    </div>
  );
};

const BannersTab = () => {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewLang, setPreviewLang] = useState(lang);

  useEffect(() => {
    axios.get(`${API}/admin/banners`)
      .then((r) => setRows(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { year: "numeric", month: "2-digit", day: "2-digit" }) : "");

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 text-sm">
          <span className="flex items-center gap-1.5 font-bold uppercase tracking-wider text-muted-foreground"><Globe size={15} weight="bold" /> {t.preview}:</span>
          <div className="flex overflow-hidden rounded border border-border">
            {["fr", "en"].map((l) => (
              <button key={l} data-testid={`preview-lang-${l}`} onClick={() => setPreviewLang(l)}
                className={`px-3 py-1 text-xs font-bold uppercase ${previewLang === l ? "bg-primary text-white" : "bg-card text-foreground hover:bg-muted"}`}>{l}</button>
            ))}
          </div>
        </div>
        <button data-testid="add-banner-button" onClick={() => navigate("/admin/website/new")}
          className="inline-flex items-center gap-2 bg-primary px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:-translate-y-0.5">
          <Plus size={16} weight="bold" /> {t.addBanner}
        </button>
      </div>

      {loading ? (
        <div className="py-16 text-center text-muted-foreground">{t.loading}</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-border px-4 py-16 text-center text-sm text-muted-foreground">{t.noBanners}</div>
      ) : (
        <div className="overflow-x-auto border border-border">
          <table className="w-full min-w-[640px] text-sm" data-testid="banners-table">
            <thead>
              <tr className="border-b border-border bg-muted text-left text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">{t.preview}</th>
                <th className="px-4 py-3">{t.bannerName}</th>
                <th className="px-4 py-3">{t.bannerType}</th>
                <th className="px-4 py-3">{t.location}</th>
                <th className="px-4 py-3">{t.creationDate}</th>
                <th className="px-4 py-3">{t.status}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => {
                const img = previewLang === "en" ? (b.image_en || b.image_fr) : (b.image_fr || b.image_en);
                return (
                  <tr key={b.id} data-testid={`banner-row-${b.id}`} onClick={() => navigate(`/admin/website/${b.id}`)}
                    className="cursor-pointer border-b border-border transition-colors duration-150 hover:bg-muted/60">
                    <td className="px-4 py-3">
                      <div className="flex h-12 w-24 items-center justify-center overflow-hidden border border-border bg-white">
                        {img ? <img src={assetSrc(img)} alt="" className="h-full w-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <ImageIcon size={18} className="text-muted-foreground" />}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-semibold text-primary">{b.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{t.graphicBanner}</td>
                    <td className="px-4 py-3 text-muted-foreground">{LOC_LABEL(t)[b.location || "carousel"] || t.locCarousel}</td>
                    <td className="px-4 py-3 text-muted-foreground">{fmtDate(b.created_at)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-bold ${b.status === "active" ? "text-primary" : "text-muted-foreground"}`}>
                        <span className={`h-2 w-2 rounded-full ${b.status === "active" ? "bg-primary" : "bg-muted-foreground"}`} />
                        {b.status === "active" ? t.active : t.disabled}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const BlogTab = () => {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/blog`)
      .then((r) => setRows(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { year: "numeric", month: "2-digit", day: "2-digit" }) : "");
  const title = (p) => (lang === "en" ? (p.title_en || p.title_fr) : (p.title_fr || p.title_en));

  return (
    <div data-testid="blog-tab">
      <div className="mb-4 flex justify-end">
        <button data-testid="add-article-button" onClick={() => navigate("/admin/website/blog/new")}
          className="inline-flex items-center gap-2 bg-primary px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:-translate-y-0.5">
          <Plus size={16} weight="bold" /> {t.addArticle}
        </button>
      </div>

      {loading ? (
        <div className="py-16 text-center text-muted-foreground">{t.loading}</div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center border border-dashed border-border bg-card px-4 py-16 text-center text-sm text-muted-foreground" data-testid="blog-empty-admin">
          <Newspaper size={40} weight="duotone" className="mb-4 text-primary" />
          {t.noArticles}
        </div>
      ) : (
        <div className="overflow-x-auto border border-border">
          <table className="w-full min-w-[640px] text-sm" data-testid="blog-table">
            <thead>
              <tr className="border-b border-border bg-muted text-left text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">{t.preview}</th>
                <th className="px-4 py-3">{t.articleTitleFr}</th>
                <th className="px-4 py-3">{t.creationDate}</th>
                <th className="px-4 py-3">{t.status}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} data-testid={`blog-row-${p.id}`} onClick={() => navigate(`/admin/website/blog/${p.id}`)}
                  className="cursor-pointer border-b border-border transition-colors duration-150 hover:bg-muted/60">
                  <td className="px-4 py-3">
                    <div className="flex h-12 w-20 items-center justify-center overflow-hidden border border-border bg-white">
                      {p.cover_image ? <img src={assetSrc(p.cover_image)} alt="" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <Newspaper size={18} className="text-muted-foreground" />}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-semibold text-primary">{title(p) || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{fmtDate(p.created_at)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-bold ${p.status === "published" ? "text-primary" : "text-muted-foreground"}`}>
                      <span className={`h-2 w-2 rounded-full ${p.status === "published" ? "bg-primary" : "bg-muted-foreground"}`} />
                      {p.status === "published" ? t.statusPublished : t.statusDraft}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const SettingsTab = () => {
  const { t } = useLang();
  const [mode, setMode] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { axios.get(`${API}/admin/coop-mode`).then((r) => setMode(r.data.mode || "sandbox")).catch(() => setMode("sandbox")); }, []);
  const setModeSaved = async (next) => {
    setSaving(true);
    const prev = mode;
    setMode(next);
    try {
      await axios.put(`${API}/admin/coop-mode`, { mode: next });
      toast.success(t.saved);
    } catch (e) { setMode(prev); toast.error(e.response?.data?.detail || t.saveFailed); }
    finally { setSaving(false); }
  };
  if (mode === null) return <div className="py-16 text-center text-muted-foreground">{t.loading}</div>;
  const isProd = mode === "production";
  return (
    <div className="max-w-2xl" data-testid="settings-tab">
      <div className="border border-border bg-card p-6">
        <div className="mb-1 flex items-center gap-2 font-display text-base font-black tracking-tight text-foreground">
          <Buildings size={18} weight="bold" className="text-primary" /> {t.coopMode}
        </div>
        <p className="mb-5 text-sm text-muted-foreground">{t.coopModeDesc}</p>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="coop-mode-switch">
          <button data-testid="coop-mode-test" disabled={saving} onClick={() => setModeSaved("sandbox")}
            className={`flex items-center gap-3 border-2 px-4 py-4 text-left transition-colors duration-200 ${!isProd ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground"}`}>
            <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${!isProd ? "border-primary" : "border-muted-foreground"}`}>
              {!isProd && <span className="h-2.5 w-2.5 rounded-full bg-primary" />}
            </span>
            <span className="text-sm font-bold text-foreground">{t.coopModeTest}</span>
          </button>

          <button data-testid="coop-mode-production" disabled={saving} onClick={() => setModeSaved("production")}
            className={`flex items-center gap-3 border-2 px-4 py-4 text-left transition-colors duration-200 ${isProd ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground"}`}>
            <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${isProd ? "border-primary" : "border-muted-foreground"}`}>
              {isProd && <span className="h-2.5 w-2.5 rounded-full bg-primary" />}
            </span>
            <span className="text-sm font-bold text-foreground">{t.coopModeProduction}</span>
          </button>
        </div>

        <div data-testid="coop-mode-banner" className={`mt-5 flex items-start gap-2.5 border px-4 py-3 text-sm ${isProd ? "border-amber-400 bg-amber-50 text-amber-800" : "border-teal-400 bg-teal-50 text-teal-800"}`}>
          {isProd ? <Warning size={18} weight="bold" className="mt-0.5 shrink-0" /> : <CheckCircle size={18} weight="bold" className="mt-0.5 shrink-0" />}
          <span className="font-medium">{isProd ? t.coopModeProdWarning : t.coopModeTestInfo}</span>
        </div>
      </div>
    </div>
  );
};

const BrandsTab = () => {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingId, setUploadingId] = useState(null);

  const load = () => axios.get(`${API}/admin/brands`).then((r) => setRows(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const addBrand = async () => {
    const { data } = await axios.post(`${API}/admin/brands`, { name: "", logo: "" });
    setRows((r) => [...r, { id: data.id, name: "", logo: "", position: r.length + 1, status: "A" }]);
  };
  const patch = (id, body) => { setRows((r) => r.map((b) => (b.id === id ? { ...b, ...body } : b))); };
  const save = (id, body) => axios.put(`${API}/admin/brands/${id}`, body).catch(() => toast.error(t.saveFailed || "Error"));
  const remove = async (id) => { await axios.delete(`${API}/admin/brands/${id}`); setRows((r) => r.filter((b) => b.id !== id)); };
  const uploadLogo = async (id, file) => {
    if (!file) return;
    setUploadingId(id);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/uploads/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      patch(id, { logo: data.url }); await save(id, { logo: data.url });
      toast.success(t.logoUpdated || "OK");
    } catch { toast.error(t.uploadFailed || "Upload failed"); } finally { setUploadingId(null); }
  };
  const move = async (idx, dir) => {
    const j = idx + dir; if (j < 0 || j >= rows.length) return;
    const next = [...rows];[next[idx], next[j]] = [next[j], next[idx]];
    setRows(next);
    await axios.post(`${API}/admin/brands/reorder`, { order: next.map((b) => b.id) }).catch(() => {});
  };

  if (loading) return <div className="py-16 text-center text-muted-foreground">{t.loading}</div>;
  return (
    <div data-testid="brands-tab">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-xl text-sm text-muted-foreground">{t.brandsManagerHint}</p>
        <button data-testid="add-brand-button" onClick={addBrand}
          className="inline-flex items-center gap-2 bg-primary px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:-translate-y-0.5">
          <Plus size={16} weight="bold" /> {t.addBrand}
        </button>
      </div>
      {rows.length === 0 ? (
        <div className="border border-dashed border-border px-4 py-16 text-center text-sm text-muted-foreground">{t.noBrands}</div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {rows.map((b, i) => (
            <div key={b.id} data-testid={`brand-row-${b.id}`} className="flex items-center gap-4 border border-border bg-card p-4">
              <div className="flex h-16 w-24 shrink-0 items-center justify-center overflow-hidden border border-border bg-white">
                {b.logo ? <img src={assetSrc(b.logo)} alt="" className="h-full w-full object-contain p-1" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <ImageIcon size={20} className="text-muted-foreground" />}
              </div>
              <div className="min-w-0 flex-1">
                <input data-testid={`brand-name-${b.id}`} value={b.name} placeholder={t.brandName}
                  onChange={(e) => patch(b.id, { name: e.target.value })} onBlur={(e) => save(b.id, { name: e.target.value })}
                  className="w-full border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <label data-testid={`brand-upload-${b.id}`} className="inline-flex cursor-pointer items-center gap-1.5 border border-border px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider text-foreground hover:border-primary hover:text-primary transition-colors duration-150">
                    <UploadSimple size={14} weight="bold" /> {uploadingId === b.id ? t.loading : (b.logo ? t.replaceLogo : t.addLogo)}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(b.id, e.target.files?.[0])} />
                  </label>
                  <button data-testid={`brand-toggle-${b.id}`} onClick={() => { const s = b.status === "H" ? "A" : "H"; patch(b.id, { status: s }); save(b.id, { status: s }); }}
                    className={`inline-flex items-center gap-1.5 border px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider transition-colors duration-150 ${b.status === "H" ? "border-amber-300 text-amber-600" : "border-emerald-300 text-emerald-600"}`}>
                    {b.status === "H" ? <EyeSlash size={14} weight="bold" /> : <Eye size={14} weight="bold" />}{b.status === "H" ? t.hidden : t.visible}
                  </button>
                </div>
              </div>
              <div className="flex shrink-0 flex-col items-center gap-1">
                <button data-testid={`brand-up-${b.id}`} disabled={i === 0} onClick={() => move(i, -1)} className="text-muted-foreground hover:text-primary disabled:opacity-30"><ArrowUp size={16} weight="bold" /></button>
                <button data-testid={`brand-down-${b.id}`} disabled={i === rows.length - 1} onClick={() => move(i, 1)} className="text-muted-foreground hover:text-primary disabled:opacity-30"><ArrowDown size={16} weight="bold" /></button>
                <button data-testid={`brand-delete-${b.id}`} onClick={() => remove(b.id)} className="mt-1 text-red-500 hover:text-red-700"><Trash size={16} weight="bold" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


export default function WebsitePage() {
  const { t } = useLang();
  const location = useLocation();
  const initialTab = new URLSearchParams(location.search).get("tab") || "banners";
  const [tab, setTab] = useState(initialTab);

  const tabs = [
    { key: "banners", label: t.banners, icon: ImageIcon },
    { key: "blog", label: t.blog, icon: Article },
    { key: "layout", label: t.layout, icon: LayoutIcon },
    { key: "brands", label: t.brands, icon: Tag },
    { key: "settings", label: t.settings, icon: Buildings },
  ];

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
        <div className="mb-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-primary">{t.website}</div>
          <h1 className="mt-1 font-display text-3xl font-black tracking-tighter text-foreground">{t.website}</h1>
        </div>

        <div className="mb-6 flex flex-wrap gap-1 border-b border-border" data-testid="website-subtabs">
          {tabs.map((tb) => {
            const Icon = tb.icon;
            const active = tab === tb.key;
            return (
              <button key={tb.key} data-testid={`website-tab-${tb.key}`} onClick={() => setTab(tb.key)}
                className={`-mb-px inline-flex items-center gap-2 border-b-2 px-4 py-3 text-xs font-bold uppercase tracking-wider transition-colors duration-200 ${active ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                <Icon size={16} weight="bold" /> {tb.label}
              </button>
            );
          })}
        </div>

        {tab === "banners" && <BannersTab />}
        {tab === "blog" && <BlogTab />}
        {tab === "layout" && <LayoutTab />}
        {tab === "brands" && <BrandsTab />}
        {tab === "settings" && <SettingsTab />}
      </main>
    </div>
  );
}
